import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import useSWR, { Fetcher } from 'swr';

import { axiosInstanceForTenantAPI } from '../../../../assets/utils/axiosInstance';
import trimText from '../../../../assets/utils/trimText';

import { useReportStatusOptions } from '~/hooks/fetcher/useReportStatusOptions';

interface PreviewCardProps {
  referenceID: string;
  decryptText: (text: string) => Promise<string>;
}

interface ReportPreviewResponse {
  id: string;
  user_full_name: string | null;
  description: {
    description: string;
  };
  status: number;
}

const PreviewCard = ({ referenceID, decryptText }: PreviewCardProps) => {
  const PREVIEW_URL_KEY = `/report/${referenceID}?is-pop-up=true`;

  const { statusOptions } = useReportStatusOptions();
  const fetcher: Fetcher<ReportPreviewResponse, string> = async (url) => {
    const response = await axiosInstanceForTenantAPI().get(url);
    return response.data;
  };

  const { data, error, isLoading } = useSWR(PREVIEW_URL_KEY, fetcher);
  const [detailsText, setDetailsText] = useState<string | null>(null);
  const [decryptStatus, setDecryptStatus] = useState(true);

  const navigate = useNavigate();

  const decryptData = useCallback(async () => {
    if (!data) {
      return null;
    }
    const processedText = await decryptText(data.description.description);
    return processedText;
  }, [data, decryptText]);

  useEffect(() => {
    const getText = async () => {
      const descriptionText = await decryptData();
      if (descriptionText === null) {
        return;
      }
      const strippedTagsText = descriptionText.replace(/<\/?[^>]*>/g, '');
      const trimmedText: string =
        trimText(strippedTagsText, 20, 200, 300)[0] + '...';
      setDetailsText(trimmedText);
      setDecryptStatus(false);
    };

    getText();
  }, [decryptData]);

  if (isLoading) {
    return (
      <Stack
        direction='row'
        justifyContent='center'
        alignItems='center'
        height={110}
      >
        <CircularProgress size={20} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack
        direction='row'
        justifyContent='center'
        alignItems='center'
        height={110}
      >
        <CircularProgress />
      </Stack>
    );
  }

  if (!data) {
    return <></>;
  }

  return (
    <Fade in>
      <Stack direction='column'>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
        >
          <Stack direction='row' spacing={1}>
            <Stack direction='column'>
              <Typography fontSize={14} color='#878787'>
                <FormattedMessage defaultMessage='Reporter' />
              </Typography>
              <Typography fontSize={16} color='black'>
                {data?.user_full_name}
              </Typography>
            </Stack>
            <Stack direction='column'>
              <Typography fontSize={14} color='#878787'>
                <FormattedMessage defaultMessage='Status' />
              </Typography>
              <Typography fontSize={16} color='black'>
                {statusOptions.find((status) => status.value === data.status)
                  ?.label ?? 'Unknown'}
              </Typography>
            </Stack>
          </Stack>
          <Button onClick={() => navigate(`/customer/reports/${referenceID}`)}>
            <FormattedMessage defaultMessage='View' />
          </Button>
        </Stack>
        <Typography color='black' fontSize={14} marginTop={2} fontWeight={500}>
          <FormattedMessage defaultMessage='Details' />
        </Typography>
        {decryptStatus ? (
          <CircularProgress size={20} sx={{ mt: 1 }} />
        ) : (
          <Fade in>
            <Typography color='#4F4F4F'>{detailsText}</Typography>
          </Fade>
        )}
      </Stack>
    </Fade>
  );
};

export default PreviewCard;
